'use client';

import { PoliceFireCrewStatus } from '@dte/otw-azure-functions/src/report/questions/problemType/EmergencyResponse';
import { ReportQuestion } from '@dte/otw-azure-functions/src/report/questions/ReportQuestion';
import { isEmptyArray } from '@dte/otw-utils-core/src/objects/isEmpty';
import { useMemo } from 'react';
import { useReportAnswerValueSelected } from '../../../../_atoms/draftReport/stepData';
import { useIsPoliceFire } from '../useIsPoliceFire';
import { useIsArcing, useIsContactWithEquipment, useIsFire, useIsInjuries } from '../useSafetyNextStep';
import { useIsPublicTrapped } from '../trappedByDTEEquipmentAtom';

export enum EmergencyCallReasons {
	ContactWithEquipment = 'Contact with energized equipment',
	PublicTrapped = 'People who are trapped',
	Injuries = 'Injuries requiring medical treatment',
	Fire = 'Fires',
	Arcing = 'Arcing',
}

export function useEmergencyCallReasons(overrideReasons?: string[]): string[] {
	const isContactWithEquipment = useIsContactWithEquipment();
	const isPublicTrapped = useIsPublicTrapped();
	const isInjuries = useIsInjuries();
	const isFire = useIsFire();
	const isArcing = useIsArcing();
	const policeFire = useIsPoliceFire();

	const reasons = useMemo(() => {
		// If police / fire report, no reason to call 911
		if (policeFire) {
			return [];
		}

		// Allow overriding the list of reasons
		if (overrideReasons) {
			return overrideReasons;
		}

		const result: string[] = [];

		if (isContactWithEquipment) {
			result.push(EmergencyCallReasons.ContactWithEquipment);
		}
		if (isPublicTrapped) {
			result.push(EmergencyCallReasons.PublicTrapped);
		}
		if (isInjuries) {
			result.push(EmergencyCallReasons.Injuries);
		}
		if (isFire) {
			result.push(EmergencyCallReasons.Fire);
		}
		if (isArcing) {
			result.push(EmergencyCallReasons.Arcing);
		}
		return result;
	}, [isContactWithEquipment, isPublicTrapped, isInjuries, isFire, isArcing, overrideReasons, policeFire]);

	return reasons;
}

export function useEmergencyServicesNeedToBeCalled(overrideReasons?: string[], alwaysShow?: boolean): boolean {
	// Is there a reason to call 911?
	const callReasons = useEmergencyCallReasons(overrideReasons);

	// Is 911 already called?
	const contacted = useReportAnswerValueSelected(ReportQuestion.EmergencyResponse, PoliceFireCrewStatus.Contacted);

	// Did they already call 911?
	if (contacted && !alwaysShow) {
		return false;
	}

	return !isEmptyArray(callReasons);
}
